import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "article";
  canonical?: string;
  siteName?: string;
  locale?: string;
  twitterSite?: string;
  noIndex?: boolean;
}

const SITE_ORIGIN = "https://www.zwahili.co.za";

const toAbsolute = (url: string) => {
  if (!url) return url;
  if (url.startsWith("http")) {
    // Always canonicalise to the www host to avoid redirect chains.
    return url.replace(/^https?:\/\/(www\.)?zwahili\.co\.za/, SITE_ORIGIN);
  }
  return `${SITE_ORIGIN}${url}`;
};

const SEO = ({
  title,
  description,
  image,
  imageAlt,
  type = "website",
  canonical,
  siteName = "Zwahili Private Game Lodge & Spa",
  locale = "en_ZA",
  twitterSite,
  noIndex = false,
}: SEOProps) => {
  const url = canonical
    ? toAbsolute(canonical)
    : typeof window !== "undefined"
      ? toAbsolute(`${window.location.pathname}`)
      : SITE_ORIGIN;
  const absoluteImage = image ? toAbsolute(image) : undefined;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:url" content={url} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {twitterSite ? <meta name="twitter:site" content={twitterSite} /> : null}

      {absoluteImage ? (
        <meta property="og:image" content={absoluteImage} />
      ) : null}
      {absoluteImage ? (
        <meta property="og:image:secure_url" content={absoluteImage} />
      ) : null}
      {absoluteImage ? (
        <meta name="twitter:image" content={absoluteImage} />
      ) : null}
      {absoluteImage && imageAlt ? (
        <meta property="og:image:alt" content={imageAlt} />
      ) : null}
      {absoluteImage && imageAlt ? (
        <meta name="twitter:image:alt" content={imageAlt} />
      ) : null}
    </Helmet>
  );
};

export default SEO;
